/** Tabla genérica: formatea cada celda según el nombre de su columna. */

import type { ReactNode } from "react";
import type { ReportColumn, TotalRow } from "../api/types";
import { StatusBadge } from "./StatusBadge";
import {
  formatCell,
  isDateField,
  isMoneyField,
  isMovementField,
  moneyClass,
} from "./format";

interface Props {
  columns: ReportColumn[];
  rows: Record<string, unknown>[];
  emptyMessage?: string;
  rowKey?: (row: Record<string, unknown>, index: number) => string;
  /**
   * Controles de la tabla —período, búsqueda, exportación—, montados EN la
   * tabla y no sueltos encima. Sueltos, la relación entre el filtro y lo que
   * filtra quedaba al criterio de quien mirara: dos cajas separadas por 16 px
   * en una pantalla que ya tiene una franja de estado y un título.
   */
  toolbar?: ReactNode;
  /** Recuento y paginación, al pie de la misma caja. */
  footer?: ReactNode;
  /**
   * La fila de total del reporte ENTERO, calculada por el servidor.
   *
   * No se suma aquí: la tabla enseña una página y sumar lo que tiene delante
   * daría un total que cambia al pasar de página. Un campo ausente en la fila
   * significa «este reporte no totaliza esta columna», y se deja en blanco —que
   * no es lo mismo que cero.
   */
  totalRow?: TotalRow | null;
}

/** La clase de la COLUMNA sale de su nombre, igual que el formato. */
function claseColumna(field: string): string | undefined {
  // Debe y Haber van sobre banda gris: son la actividad del período, entre dos
  // saldos que son situación.
  if (isMovementField(field)) return "num mov";
  if (isMoneyField(field)) return "num";
  // Una fecha no se parte: «2026-01-01» quedaba como «2026-01-» / «01» en
  // cuanto otra columna reclamaba ancho.
  if (isDateField(field)) return "fecha";
  return undefined;
}

/** La de la CELDA añade el signo: el negativo se tiñe y el cero se apaga. */
function claseCelda(field: string, value: unknown): string | undefined {
  if (!isMoneyField(field)) return claseColumna(field);
  return isMovementField(field) ? `${moneyClass(value)} mov` : moneyClass(value);
}

export function DataTable({
  columns,
  rows,
  emptyMessage,
  rowKey,
  toolbar,
  footer,
  totalRow,
}: Props) {
  /* Hasta dónde llega el rótulo del total: hasta la primera columna que el
     total TOTALIZA.

     El criterio es «tiene valor en la fila de total», no «parece dinero». Con
     `isMoneyField` el rótulo se habría comido «Documentos» en los Anexos F-07
     —un recuento, que el reporte sí totaliza pero que no lleva ninguna de las
     pistas de importe—, y esas 427 filas contadas habrían desaparecido del pie
     sin que nada lo dijera. Un campo ausente en la fila significa «este reporte
     no totaliza esta columna», y esa es justo la pregunta que hay que hacer. */
  const primeraCifra = columns.findIndex(
    (column) => totalRow?.[column.field] !== undefined,
  );
  const cabecera = primeraCifra > 0 ? primeraCifra : 1;

  return (
    <section className="tabla">
      {toolbar && <div className="tabla-toolbar">{toolbar}</div>}

      {rows.length === 0 ? (
        <div className="state">
          <p>{emptyMessage ?? "Sin filas para el período seleccionado."}</p>
        </div>
      ) : (
        <div className="table-wrap">
          {/* `datos` marca la tabla GENÉRICA de un reporte, la que no tiene
              forma de documento. Le da un suelo por columna para que, cuando
              falte ancho, la caja se desplace en vez de estrujarlo todo. */}
          <table className="datos">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.field}
                    className={claseColumna(column.field)}
                    scope="col"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={rowKey ? rowKey(row, index) : index}>
                  {columns.map((column) => (
                    <td
                      key={column.field}
                      className={claseCelda(column.field, row[column.field])}
                    >
                      {formatCell(column.field, row[column.field])}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Un hueco del alto de la fila de total. La fila va pegada al
                  fondo del contenedor que desplaza (`position: sticky`), así
                  que TAPABA la última fila de datos: en el Libro Diario se veía
                  «TOTALES DEL PERÍODO» impreso encima del último asiento. Con
                  el hueco, la última fila sube por encima del total y se lee
                  entera, y el total sigue estando siempre a la vista. */}
              {totalRow && (
                <tr className="fila-hueco" aria-hidden="true">
                  <td colSpan={columns.length} />
                </tr>
              )}
            </tbody>
            {totalRow && (
              <tfoot>
                <tr className="fila-total">
                  {/* El rótulo ocupa TODAS las columnas de texto que preceden a
                      la primera cifra. Metido solo en la primera —«Fecha», que
                      mide 90 px— «TOTALES DEL PERÍODO» se partía en dos líneas,
                      y la doble raya del total salía en trozos: un tramo bajo
                      el rótulo, nada bajo las columnas vacías de en medio
                      —`td:empty` les quita el filete— y otro tramo bajo las
                      cifras. Un total contable se subraya de una sola pasada. */}
                  <td colSpan={cabecera} className="fila-total-rotulo">
                    {totalRow.__label__}
                  </td>
                  {columns.slice(cabecera).map((column) => {
                    const valor = totalRow[column.field];
                    return (
                      <td
                        key={column.field}
                        className={
                          valor === undefined ? undefined : claseCelda(column.field, valor)
                        }
                      >
                        {valor === undefined ? "" : formatCell(column.field, valor)}
                      </td>
                    );
                  })}
                </tr>
                {/* Un total cuyo importe va en UNA columna con el lado en otra:
                    las dos cifras no caben en la fila del total, así que cada
                    una va en la suya, con la cifra bajo la columna que la propia
                    línea nombra. */}
                {totalRow.__breakdown__?.map((linea) => (
                  <tr className="fila-total-desglose" key={linea.label}>
                    {columns.map((column, indice) => {
                      if (indice === 0) {
                        return <td key={column.field}>{linea.label}</td>;
                      }
                      if (column.field !== linea.field) {
                        return <td key={column.field} />;
                      }
                      return (
                        <td
                          key={column.field}
                          className={claseCelda(column.field, linea.amount)}
                        >
                          {formatCell(column.field, linea.amount)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* Por qué unas columnas van en blanco, y si el cuadre salió.
                    Un Debe distinto del Haber no es un detalle de formato: dice
                    que al libro le falta capturar una cuenta. */}
                {(totalRow.__note__ || totalRow.__balanced__ === false) && (
                  <tr className="fila-total-nota">
                    <td colSpan={columns.length} className="small">
                      {totalRow.__balanced__ === false && (
                        <StatusBadge tone="serious">
                          El Debe no iguala al Haber
                        </StatusBadge>
                      )}{" "}
                      {totalRow.__note__}
                    </td>
                  </tr>
                )}
              </tfoot>
            )}
          </table>
        </div>
      )}

      {footer && <div className="tabla-foot">{footer}</div>}
    </section>
  );
}
